import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { graphqlRequest } from './graphql'
import type { Order } from './orders'
import type {
  OrderStatus,
  UpdateOrderStatusInput as SharedUpdateOrderStatusInput,
  OrderStatusHistoryEntry as SharedOrderStatusHistoryEntry,
} from '@shinkansen/graphql'

// Re-export types from shared package for convenience
export type { OrderStatus }

/**
 * Input for updating order status - mapped from shared package
 * Note: The shared type uses 'orderId' as UUID input
 */
export type UpdateOrderStatusInput = SharedUpdateOrderStatusInput

/**
 * Response from update order status mutation
 */
interface UpdateOrderStatusResponse {
  id: string
  status: string
  updatedAt: string
}

const ORDER_FIELDS = `
  id
  externalId
  customerId
  status
  totalAmount
  deliveryFee
  currency
  deliveryAddress
  paymentStatus
  paymentUrl
  paymentMethod
  paymentChannel
  paymentProvider
  paymentProviderReference
  paymentTransactionId
  customer {
    name
    phone
  }
  createdAt
  updatedAt
`

/**
 * Hook to fetch all orders for admin dashboard.
 * Requires admin role - permission check is handled by backend.
 * 
 * @param status - Optional filter by order status (PENDING, IN_TRANSIT, DELIVERED, etc.)
 * @returns TanStack Query result with orders array
 */
export function useAllOrders(status?: string) {
  return useQuery({
    queryKey: ['allOrders', status],
    queryFn: async () => {
      const result = await graphqlRequest<{ allOrders: Order[] }>(`
        query AllOrders($status: OrderStatus) {
          allOrders(status: $status) {
            ${ORDER_FIELDS}
          }
        }
      `, { status })
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data?.allOrders ?? []
    },
  })
}

/**
 * Hook to update order status.
 * Requires admin role - permission check is handled by backend.
 * 
 * @returns TanStack Mutation for updating order status
 */
export function useUpdateOrderStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: UpdateOrderStatusInput) => {
      const result = await graphqlRequest<{ updateOrderStatus: UpdateOrderStatusResponse }>(`
        mutation UpdateOrderStatus($input: UpdateOrderStatusInput!) {
          updateOrderStatus(input: $input) {
            id
            status
            updatedAt
          }
        }
      `, { input })
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data?.updateOrderStatus
    },
    onSuccess: (data) => {
      // Invalidate all orders list
      queryClient.invalidateQueries({ queryKey: ['allOrders'] })
      // Invalidate specific order if data is returned
      if (data?.id) {
        queryClient.invalidateQueries({ queryKey: ['order', data.id] })
        queryClient.invalidateQueries({ queryKey: ['orderStatusHistory', data.id] })
      }
    },
  })
}

/**
 * Input for assigning a driver to an order
 */
export interface AssignDriverInput {
  orderId: string
  driverId: string
}

/**
 * Hook to assign a driver to an order.
 * Requires OrderAssign permission - permission check is handled by backend.
 * 
 * @returns TanStack Mutation for assigning driver to order
 */
export function useAssignDriver() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ orderId, driverId }: AssignDriverInput) => {
      const result = await graphqlRequest<{ assignDriver: { id: string; assignedDriverId: string; status: string } }>(`
        mutation AssignDriver($orderId: UUID!, $driverId: UUID!) {
          assignDriver(orderId: $orderId, driverId: $driverId) {
            id
            assignedDriverId
            status
          }
        }
      `, { orderId, driverId })
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data?.assignDriver
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['allOrders'] })
      queryClient.invalidateQueries({ queryKey: ['availableDrivers'] })
      if (data?.id) {
        queryClient.invalidateQueries({ queryKey: ['order', data.id] })
        queryClient.invalidateQueries({ queryKey: ['orderStatusHistory', data.id] })
      }
    },
  })
}

/**
 * Status history entry for an order - using shared type
 */
export type OrderStatusHistoryEntry = SharedOrderStatusHistoryEntry

/**
 * Hook to fetch status history for an order.
 * Shows all status changes with who made them and when.
 * 
 * @param orderId - The order ID to fetch history for
 * @returns TanStack Query result with status history array
 */
export function useOrderStatusHistory(orderId: string) {
  return useQuery({
    queryKey: ['orderStatusHistory', orderId],
    queryFn: async () => {
      const result = await graphqlRequest<{ orderStatusHistory: OrderStatusHistoryEntry[] }>(`
        query OrderStatusHistory($orderId: UUID!) {
          orderStatusHistory(orderId: $orderId) {
            id
            orderId
            fromStatus
            toStatus
            changedByUserId
            changedAt
            notes
          }
        }
      `, { orderId })
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data?.orderStatusHistory ?? []
    },
    enabled: !!orderId,
  })
}
